import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Logo } from '@/components/brand/logo'
import { FormAlert } from '@/components/app/form'
import { Button } from '@/components/ui/button'
import { authClient, authErrorMessage } from '@/lib/auth-client'
import { sessionQuery } from '@/lib/session'

export const Route = createFileRoute('/invite/$id')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(sessionQuery).catch(() => null)
    if (!session) throw redirect({ to: '/sign-up', search: { redirect: location.pathname } })
    return { session }
  },
  head: () => ({ meta: [{ title: 'Join a Vault · Nebueye' }] }),
  component: AcceptInvite,
})

function AcceptInvite() {
  const { id } = Route.useParams()
  const { session } = Route.useRouteContext()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState<'accept' | 'decline'>()

  const invite = useQuery({
    queryKey: ['invitation', id],
    queryFn: async () => {
      const { data, error } = await authClient.organization.getInvitation({ query: { id } })
      if (error) throw error
      return data
    },
    retry: false,
  })

  async function respond(kind: 'accept' | 'decline') {
    setPending(kind)
    const { error } =
      kind === 'accept'
        ? await authClient.organization.acceptInvitation({ invitationId: id })
        : await authClient.organization.rejectInvitation({ invitationId: id })
    setPending(undefined)
    if (error) return setError(authErrorMessage(error))
    await queryClient.invalidateQueries()
    navigate({ to: kind === 'accept' ? '/' : '/onboarding' })
  }

  return (
    <div className="grid min-h-dvh place-items-center px-6 py-12">
      <div className="w-full max-w-[420px] space-y-8">
        <Logo />
        {invite.isPending ? (
          <Loader2 className="text-muted-foreground size-5 animate-spin" aria-label="Loading invitation" />
        ) : invite.isError || !invite.data ? (
          <div className="space-y-3">
            <h1 className="text-headline font-semibold">This invitation isn't available</h1>
            <p className="text-muted-foreground text-[15px] leading-6">
              It may have expired, been cancelled, or been sent to a different email than{' '}
              <span className="text-foreground font-medium">{session.user.email}</span>. Ask for a new invite.
            </p>
            <Button asChild variant="outline">
              <Link to="/">Go to dashboard</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-2">
              <h1 className="text-headline font-semibold">Join {invite.data.organizationName}</h1>
              <p className="text-muted-foreground text-[15px] leading-6">
                {invite.data.inviterEmail} invited you as {invite.data.role === 'admin' ? 'an admin' : `a ${invite.data.role}`}.
                You'll see the Vault's orders, drops and deliveries.
              </p>
            </div>
            {error && <FormAlert>{error}</FormAlert>}
            <div className="flex gap-2">
              <Button size="lg" onClick={() => respond('accept')} disabled={!!pending}>
                {pending === 'accept' && <Loader2 className="animate-spin" />}
                Accept and join
              </Button>
              <Button size="lg" variant="ghost" onClick={() => respond('decline')} disabled={!!pending}>
                Decline
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
